import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Check, X, ExternalLink, Download } from "lucide-react";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";
const MAX_BYTES = 25 * 1024 * 1024;

const STATUS_STYLE = {
  pending: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
  approved: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
  rejected: "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:text-red-300",
};

export const DOC_TYPE_LABELS = {
  // dealer
  dealer_license: "Dealer License",
  finance_license: "Finance License",
  ein_document: "EIN Document",
  w9: "W-9",
  certificate_of_insurance: "Certificate of Insurance",
  // user
  drivers_license: "Driver's License",
  proof_of_income: "Proof of Income",
  proof_of_insurance: "Proof of Insurance",
  proof_of_residence: "Proof of Residence",
  ssn_verification: "SSN Verification",
  // note
  vehicle_photo: "Vehicle Photo",
  ric: "Retail Installment Contract",
  payment_history: "Payment History",
  title: "Title",
  insurance: "Insurance",
  inspection: "Inspection",
  assignment: "Assignment",
  additional: "Additional",
};

export function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const downloadUrl = (docId) => `${BACKEND_URL}/api/documents/${docId}/download`;

/** Auth-aware blob URL hook for images (img tag cannot send Bearer headers, but cookies work cross-domain when withCredentials). */
function useAuthedBlob(docId, contentType) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!docId || !contentType?.startsWith("image/")) return;
    let revoke;
    let cancelled = false;
    api.get(`/documents/${docId}/download`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(r.data);
        revoke = blobUrl;
        setUrl(blobUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [docId, contentType]);
  return url;
}

function StatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 border text-[10px] font-overline ${STATUS_STYLE[status] || ""}`} data-testid={`doc-status-${status}`}>
      {status}
    </span>
  );
}

function PreviewModal({ doc, open, onClose }) {
  const blobUrl = useAuthedBlob(open ? doc?.document_id : null, doc?.content_type);
  if (!doc) return null;
  const isPdf = doc.content_type === "application/pdf";
  const isImg = (doc.content_type || "").startsWith("image/");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-4xl" data-testid="doc-preview-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl truncate">{doc.file_name}</DialogTitle>
        </DialogHeader>
        <div className="border border-border bg-muted/30 min-h-[400px] max-h-[70vh] overflow-auto flex items-center justify-center">
          {isImg && blobUrl && (
            <img src={blobUrl} alt={doc.file_name} className="max-w-full max-h-[68vh]" data-testid="doc-preview-image" />
          )}
          {isImg && !blobUrl && <div className="text-muted-foreground text-sm py-12">Loading preview…</div>}
          {isPdf && (
            <iframe
              title={doc.file_name}
              src={downloadUrl(doc.document_id)}
              className="w-full h-[68vh] bg-white"
              data-testid="doc-preview-pdf"
            />
          )}
          {!isImg && !isPdf && (
            <div className="text-sm text-muted-foreground py-12">
              No inline preview available.
              <a className="ml-1 underline" href={downloadUrl(doc.document_id)} target="_blank" rel="noreferrer">Open file</a>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button asChild variant="outline" className="rounded-none">
            <a href={downloadUrl(doc.document_id)} target="_blank" rel="noreferrer" data-testid="doc-open-tab">
              <ExternalLink className="w-4 h-4 mr-1" /> Open in tab
            </a>
          </Button>
          <Button asChild className="rounded-none">
            <a href={downloadUrl(doc.document_id)} download={doc.file_name} data-testid="doc-download-link">
              <Download className="w-4 h-4 mr-1" /> Download
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Compact card showing one document with view + (optional) admin actions. */
export function DocumentCard({ doc, isAdmin, onChanged, allowDelete }) {
  const [open, setOpen] = useState(false);
  const [rejectingOpen, setRejectingOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isImg = (doc.content_type || "").startsWith("image/");
  const blobUrl = useAuthedBlob(isImg ? doc.document_id : null, doc.content_type);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/documents/${doc.document_id}/approve`);
      toast.success("Approved");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (reason.trim().length < 3) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/documents/${doc.document_id}/reject`, { reason: reason.trim() });
      toast.success("Rejected");
      setRejectingOpen(false); setReason("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${doc.document_id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <div className="border border-border p-3 flex gap-3 items-start" data-testid={`doc-card-${doc.document_id}`}>
        <button
          onClick={() => setOpen(true)}
          className="w-16 h-16 border border-border bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden hover:border-primary"
          data-testid={`doc-thumb-${doc.document_id}`}
        >
          {isImg && blobUrl ? (
            <img src={blobUrl} alt="" className="w-full h-full object-cover" />
          ) : doc.content_type === "application/pdf" ? (
            <FileText className="w-6 h-6 text-muted-foreground" />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setOpen(true)} className="font-mono-data text-sm truncate hover:text-primary"
              data-testid={`doc-name-${doc.document_id}`}>
              {doc.file_name}
            </button>
            <StatusPill status={doc.status} />
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span data-testid={`doc-type-${doc.document_id}`}>{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</span>
            <span>·</span>
            <span>{fmtBytes(doc.size_bytes)}</span>
            <span>·</span>
            <span>by {doc.uploaded_by_name || "—"}</span>
            <span>·</span>
            <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
          </div>
          {doc.status === "rejected" && doc.reject_reason && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-1" data-testid={`doc-reject-reason-${doc.document_id}`}>
              Reason: {doc.reject_reason}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          {isAdmin && doc.status === "pending" && (
            <>
              <Button size="sm" className="rounded-none h-7 text-[11px]" onClick={approve} disabled={busy}
                data-testid={`doc-approve-${doc.document_id}`}>
                <Check className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="destructive" className="rounded-none h-7 text-[11px]"
                onClick={() => setRejectingOpen(true)} disabled={busy}
                data-testid={`doc-reject-${doc.document_id}`}>
                <X className="w-3 h-3 mr-1" /> Reject
              </Button>
            </>
          )}
          {allowDelete && doc.status !== "approved" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]" onClick={del}
              data-testid={`doc-delete-${doc.document_id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      <PreviewModal doc={doc} open={open} onClose={() => setOpen(false)} />

      <Dialog open={rejectingOpen} onOpenChange={(v) => !v && setRejectingOpen(false)}>
        <DialogContent className="rounded-none max-w-md" data-testid={`doc-reject-dialog-${doc.document_id}`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Reject Document</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Tell the uploader what to fix.</p>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              className="rounded-none" placeholder="e.g. Document is blurry; please re-upload"
              data-testid={`doc-reject-reason-input-${doc.document_id}`} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setRejectingOpen(false)}>Cancel</Button>
            <Button variant="destructive" className="rounded-none" onClick={reject} disabled={busy}
              data-testid={`doc-reject-confirm-${doc.document_id}`}>
              {busy ? "…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Upload widget + list. Pass an entityType/entityId and one or more docTypes.
 *
 * Props:
 *   entityType: 'dealer_profile' | 'loan_application' | 'note' | 'assignment' | 'user'
 *   entityId: string
 *   docTypes: [{value, label, multiple?: boolean}]   // controls dropdown
 *   readOnly?: boolean
 *   isAdmin?: boolean
 *   showAll?: boolean   // admin view across docTypes
 */
export default function DocumentUpload({
  entityType, entityId, docTypes = [], readOnly = false,
  isAdmin = false, showAll = false, title = "Documents",
}) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedType, setSelectedType] = useState(docTypes[0]?.value || "additional");
  const fileRef = useRef(null);

  const load = async () => {
    if (!entityId) { setDocs([]); return; }
    try {
      const r = await api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } });
      setDocs(r.data.items || []);
    } catch (e) {
      // Silent on 403 (entity not yet created)
      setDocs([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const pick = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 25 MB limit");
      return;
    }
    const ok = ["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(file.type);
    if (!ok) {
      toast.error("Only PDF, JPG, JPEG, PNG allowed");
      return;
    }
    if (!entityId) { toast.error("Save the entity first before uploading documents"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("doc_type", selectedType);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — pending review");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const groupedDocs = useMemo(() => {
    if (showAll) return { __all: docs };
    const g = {};
    docTypes.forEach((t) => { g[t.value] = []; });
    docs.forEach((d) => {
      if (!g[d.doc_type]) g[d.doc_type] = [];
      g[d.doc_type].push(d);
    });
    return g;
  }, [docs, docTypes, showAll]);

  return (
    <div className="space-y-4" data-testid={`doc-upload-${entityType}`}>
      <div className="flex items-center justify-between">
        <div className="font-overline">{title}</div>
        {!readOnly && (
          <div className="flex gap-2 items-center">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="border border-border rounded-none bg-background px-2 py-1.5 text-xs font-overline"
              data-testid={`doc-type-select-${entityType}`}
            >
              {docTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid={`doc-upload-input-${entityType}`}
            />
            <Button onClick={pick} disabled={busy || !entityId} className="rounded-none uppercase text-xs tracking-wider"
              data-testid={`doc-upload-btn-${entityType}`}>
              <Upload className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : "Upload"}
            </Button>
          </div>
        )}
      </div>

      {!entityId && !readOnly && (
        <div className="border border-dashed border-border p-4 text-xs text-muted-foreground">
          Save your record first — documents attach once the record exists.
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Accepted: PDF, JPG, JPEG, PNG · Max 25 MB · Documents are reviewed by admin before approval.
      </p>

      {showAll ? (
        docs.length === 0 ? (
          <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
            data-testid={`doc-list-empty-${entityType}`}>No documents uploaded yet.</div>
        ) : (
          <div className="grid gap-2">
            {docs.map((d) => <DocumentCard key={d.document_id} doc={d} isAdmin={isAdmin} onChanged={load} allowDelete={!readOnly} />)}
          </div>
        )
      ) : (
        docTypes.map((t) => {
          const list = groupedDocs[t.value] || [];
          return (
            <div key={t.value} className="border border-border" data-testid={`doc-section-${t.value}`}>
              <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between">
                <div className="font-overline text-xs">{t.label}</div>
                <div className="text-[10px] font-mono-data text-muted-foreground">
                  {list.length} file{list.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="p-2 space-y-2">
                {list.length === 0 ? (
                  <div className="text-xs text-muted-foreground px-2 py-3">No file uploaded.</div>
                ) : list.map((d) => (
                  <DocumentCard key={d.document_id} doc={d} isAdmin={isAdmin} onChanged={load} allowDelete={!readOnly} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
